import React, { useMemo } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { makeStyles } from 'tss-react/mui'
import { nullsFirst } from '@e3dc-react/shell/Libs/SortAlgorithms'
import { TableComplete } from '@e3dc-react/shell/Elements/Table'
import EMobilityChargingCardTableCheckIcon from './EMobilityChargingCardTableCheckIcon'
import EMobilityChargingCardTableMenuIcon from './EMobilityChargingCardTableMenuIcon'
import { EMobilityChargingCardsDataModel, EMobilityChargingCardsTableDataModel } from './models/e-mobility-charging-cards-models'
import { Paper } from '@mui/material'

const useStyles = makeStyles()(theme => ({
  tableHeader: {
    textTransform: 'uppercase',
    whiteSpace: 'pre-line',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    paddingTop: theme.spacing(2),
  },
  row: {
    '&.MuiTableRow-root': {
      td: {
        height: 58,
      },
    },
  },
}))

interface EMobilityChargingCardsTableProps {
  data: EMobilityChargingCardsDataModel[]
}

const EMobilityChargingCardsTable: React.FC<EMobilityChargingCardsTableProps> = ({ data }): React.ReactElement => {
  const { classes } = useStyles()
  const { t } = useCustomTranslation()

  const tableHeader = useMemo(() => {
    return [
      {
        value: 'name',
        label: t('emobility.chargingCards.chargingCard'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'vehicleName',
        label: t('emobility.chargingCards.vehicle'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'status',
        label: t('emobility.chargingCards.status.status'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'createdAt',
        label: t('emobility.chargingCards.created'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      {
        value: 'updatedAt',
        label: t('emobility.chargingCards.changed'),
        width: 'auto',
        sortFunction: nullsFirst((a: string, b: string) => a.localeCompare(b)),
      },
      { value: 'checkIcon', label: '', width: 60 },
      { value: 'menuIcon', label: '', width: 60 },
    ]
  }, [t])

  const tableData: EMobilityChargingCardsTableDataModel[] = data.map(card => {
    return {
      name: card.name,
      vehicleName: card.vehicleName,
      createdAt: card.createdAt,
      updatedAt: card.updatedAt,
      status: t(`emobility.chargingCards.status.${card.status}`),
      checkIcon: <EMobilityChargingCardTableCheckIcon />,
      menuIcon: <EMobilityChargingCardTableMenuIcon data={card} storageId={card.storageId.toString()} />,
    }
  })

  return (
    <Paper className={classes.tableContainer}>
      <TableComplete
        header={tableHeader}
        data={tableData}
        bodyElevation={0}
        footerElevation={0}
        headerElevation={0}
        rowClassName={classes.row}
        headerClassName={classes.tableHeader}
        coloredColumn
      />
    </Paper>
  )
}

export default EMobilityChargingCardsTable
